import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { firstValueFrom } from "rxjs";
import { CONSTANT } from "src/app/constant";
import { DraftItem, FinalRatingsData } from "src/app/models/models";
import { DraftService } from "src/app/shared/services/draft.service";
import { IObservationRating, OfflineRatingService } from "./offline-rating.service";

@Component({
	selector: "app-test-offline-rating",
	standalone: true,
	imports: [CommonModule, FormsModule],
	template: `
		<div class="container">
			<h1>OfflineRatingService Test Page</h1>
			<p class="subtitle">Test the OfflineRatingService using the actual service implementation</p>

			<!-- Main Calculate Method Test -->
			<div class="section">
				<h2>1. Test Calculate Method (Full Calculation)</h2>

				<div
					style="margin-bottom: 15px; padding: 15px; background: #e7f3ff; border-radius: 4px; border: 1px solid #b3d9ff;"
				>
					<h3 style="margin-top: 0; color: #0066cc;">Load Draft from IndexedDB</h3>
					<label for="draftSelect">Select Draft:</label>
					<select
						id="draftSelect"
						[(ngModel)]="selectedDraftId"
						(change)="onDraftSelected()"
						style="width: 100%; padding: 8px; margin-bottom: 10px;"
					>
						<option value="">-- Select a draft --</option>
						@for (draft of drafts; track draft) {
							<option [value]="draft.id">
								{{ getDraftDisplayText(draft) }}
							</option>
						}
					</select>
					<button (click)="fetchDraftsFromIndexedDB()" [disabled]="isLoadingDrafts">
						{{ isLoadingDrafts ? "Loading..." : "Refresh Drafts List" }}
					</button>
					@if (drafts.length === 0 && !isLoadingDrafts) {
						<div style="margin-top: 10px; color: #666;">
							No drafts found in IndexedDB. Click "Refresh Drafts List" to load.
						</div>
					}
				</div>

				<label for="candidateId">Candidate ID:</label>
				<input type="text" id="candidateId" [(ngModel)]="candidateId" placeholder="e.g., candidate-123" />

				<label for="draftData" style="margin-top: 15px;">Draft Data (JSON):</label>
				<textarea
					id="draftData"
					[(ngModel)]="draftDataJson"
					(ngModelChange)="onDraftJsonChange($event)"
					placeholder="Paste your draft JSON here or select from IndexedDB above..."
					rows="10"
				></textarea>

				<button (click)="testCalculate()">Calculate Ratings</button>
				<button (click)="loadSampleData()">Load Sample Data</button>

				@if (calculateResult) {
					<div class="results">
						<h3>Calculation Results</h3>
						<div class="result-item success"><strong>Rating A:</strong> {{ calculateResult.ratingA }}</div>
						<div class="result-item success"><strong>Rating B:</strong> {{ calculateResult.ratingB }}</div>
						<div class="result-item success"><strong>Rating C:</strong> {{ calculateResult.ratingC }}</div>
						<div class="result-item success"><strong>Final Rating:</strong> {{ calculateResult.finalRating }}</div>
						<div class="json-preview">{{ calculateResult | json }}</div>
					</div>
				}

				@if (calculateError) {
					<div class="result-item error"><strong>Error:</strong> {{ calculateError }}</div>
				}
			</div>

			<!-- Individual Method Tests -->
			<div class="section">
				<h2>2. Test Individual Rating Methods</h2>
				<div class="method-tests">
					<div class="method-test">
						<h4>Rating A</h4>
						<label>Predefined Observations (JSON):</label>
						<textarea
							[(ngModel)]="ratingA_predefined"
							placeholder='[{"observationCode":"OB1","maneuverCode":"M1","totalAttempt":2,"activeAttempt":2,"isRemoved":false}]'
							rows="4"
						></textarea>
						<label>New Observations (JSON):</label>
						<textarea
							[(ngModel)]="ratingA_new"
							placeholder='[{"observationCode":"OB2","maneuverCode":"M1","totalAttempt":1,"activeAttempt":1,"isRemoved":false}]'
							rows="4"
						></textarea>
						<label>Maneuver Codes to Exclude (comma-separated):</label>
						<input type="text" [(ngModel)]="ratingA_exclude" placeholder="M2,M3" />
						<button (click)="testRatingA()">Calculate Rating A</button>
						@if (ratingAResult !== null) {
							<div class="rating-display">Rating A: {{ ratingAResult }}</div>
						}
						@if (ratingAError) {
							<div class="result-item error">{{ ratingAError }}</div>
						}
					</div>

					<div class="method-test">
						<h4>Rating B</h4>
						<label>Predefined Observations (JSON):</label>
						<textarea
							[(ngModel)]="ratingB_predefined"
							placeholder='[{"observationCode":"OB1","maneuverCode":"M1","totalAttempt":2,"activeAttempt":2,"isRemoved":false}]'
							rows="4"
						></textarea>
						<label>New Observations (JSON):</label>
						<textarea
							[(ngModel)]="ratingB_new"
							placeholder='[{"observationCode":"OB2","maneuverCode":"M1","totalAttempt":1,"activeAttempt":1,"isRemoved":false}]'
							rows="4"
						></textarea>
						<label>Maneuver Codes to Exclude (comma-separated):</label>
						<input type="text" [(ngModel)]="ratingB_exclude" placeholder="M2,M3" />
						<button (click)="testRatingB()">Calculate Rating B</button>
						@if (ratingBResult !== null) {
							<div class="rating-display">Rating B: {{ ratingBResult }}</div>
						}
						@if (ratingBError) {
							<div class="result-item error">{{ ratingBError }}</div>
						}
					</div>

					<div class="method-test">
						<h4>Rating C</h4>
						<label>Scores Array (comma-separated numbers):</label>
						<input type="text" [(ngModel)]="ratingC_scores" placeholder="3,4,5,4,3" />
						<button (click)="testRatingC()">Calculate Rating C</button>
						@if (ratingCResult !== null) {
							<div class="rating-display">Rating C: {{ ratingCResult }}</div>
						}
						@if (ratingCError) {
							<div class="result-item error">{{ ratingCError }}</div>
						}
					</div>
				</div>
			</div>
		</div>
	`,
	changeDetection: ChangeDetectionStrategy.Eager,
	styles: [
		`
			.container {
				max-width: 1200px;
				margin: 0 auto;
				padding: 30px;
			}

			h1 {
				color: #333;
				margin-bottom: 10px;
			}

			.subtitle {
				color: #666;
				margin-bottom: 30px;
			}

			.section {
				margin-bottom: 30px;
				padding: 20px;
				background: #fafafa;
				border-radius: 6px;
				border: 1px solid #e0e0e0;
			}

			.section h2 {
				color: #444;
				margin-bottom: 15px;
				font-size: 1.2em;
			}

			label {
				display: block;
				margin-bottom: 8px;
				font-weight: 500;
				color: #555;
			}

			input,
			textarea {
				width: 100%;
				padding: 10px;
				border: 1px solid #ddd;
				border-radius: 4px;
				font-family: "Monaco", "Menlo", "Courier New", monospace;
				font-size: 13px;
				margin-bottom: 10px;
			}

			textarea {
				min-height: 100px;
				resize: vertical;
			}

			button {
				background: #007bff;
				color: white;
				border: none;
				padding: 12px 24px;
				border-radius: 4px;
				cursor: pointer;
				font-size: 14px;
				font-weight: 500;
				margin-right: 10px;
				margin-top: 10px;
			}

			button:hover {
				background: #0056b3;
			}

			.results {
				margin-top: 20px;
				padding: 15px;
				background: white;
				border-radius: 4px;
				border: 1px solid #ddd;
			}

			.results h3 {
				color: #333;
				margin-bottom: 10px;
			}

			.result-item {
				padding: 10px;
				margin: 8px 0;
				background: #f8f9fa;
				border-left: 3px solid #007bff;
				border-radius: 3px;
			}

			.result-item strong {
				color: #007bff;
			}

			.error {
				background: #fee;
				border-left-color: #dc3545;
				color: #721c24;
			}

			.error strong {
				color: #dc3545;
			}

			.success {
				background: #d4edda;
				border-left-color: #28a745;
			}

			.json-preview {
				background: #f8f9fa;
				padding: 10px;
				border-radius: 4px;
				margin-top: 10px;
				font-family: "Monaco", "Menlo", "Courier New", monospace;
				font-size: 12px;
				max-height: 300px;
				overflow-y: auto;
				white-space: pre-wrap;
				word-wrap: break-word;
			}

			.method-tests {
				display: grid;
				grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
				gap: 15px;
				margin-top: 15px;
			}

			.method-test {
				padding: 15px;
				background: white;
				border: 1px solid #ddd;
				border-radius: 4px;
			}

			.method-test h4 {
				margin-bottom: 10px;
				color: #555;
			}

			.rating-display {
				font-size: 1.5em;
				font-weight: bold;
				color: #007bff;
				margin-top: 10px;
			}
		`,
	],
})
export class TestOfflineRatingComponent {
	candidateId: string = "candidate-1";
	draftDataJson: string = "";
	calculateResult: FinalRatingsData | null = null;
	calculateError: string | null = null;

	drafts: DraftItem[] = [];
	selectedDraftId: string = "";
	isLoadingDrafts: boolean = false;

	ratingA_predefined: string = "";
	ratingA_new: string = "";
	ratingA_exclude: string = "";
	ratingAResult: number | null = null;
	ratingAError: string | null = null;

	ratingB_predefined: string = "";
	ratingB_new: string = "";
	ratingB_exclude: string = "";
	ratingBResult: number | null = null;
	ratingBError: string | null = null;

	ratingC_scores: string = "3,4,5,4,3";
	ratingCResult: number | null = null;
	ratingCError: string | null = null;

	constructor(
		private offlineRatingService: OfflineRatingService,
		private draftService: DraftService,
		private dbService: NgxIndexedDBService,
	) {
		// Auto-fetch drafts on component initialization
		this.fetchDraftsFromIndexedDB();
	}

	testCalculate() {
		this.calculateResult = null;
		this.calculateError = null;

		if (!this.candidateId) {
			this.calculateError = "Please enter a candidate ID";
			return;
		}

		if (!this.draftDataJson.trim()) {
			this.calculateError = "Please enter draft data";
			return;
		}

		try {
			const draft: DraftItem = JSON.parse(this.draftDataJson);
			// Set the draft in DraftService using type assertion to access private property
			// This is necessary for testing since DraftService doesn't expose a public setter
			(this.draftService as any)._draft = draft;
			(this.draftService as any)._inDraftMode = true;

			// Uses the actual OfflineRatingService - any changes to the service will be reflected here
			this.calculateResult = this.offlineRatingService.calculate(this.candidateId);
		} catch (error: any) {
			this.calculateError = error.message || "Failed to calculate ratings";
		}
	}

	testRatingA() {
		this.ratingAResult = null;
		this.ratingAError = null;

		try {
			const predefined: IObservationRating[] = JSON.parse(this.ratingA_predefined || "[]");
			const newObs: IObservationRating[] = JSON.parse(this.ratingA_new || "[]");
			const exclude: string[] = this.ratingA_exclude
				.split(",")
				.map((s) => s.trim())
				.filter((s) => s);

			// Uses the actual OfflineRatingService - any changes to the service will be reflected here
			this.ratingAResult = this.offlineRatingService.calculateRatingA({
				predefinedObs: predefined,
				newObs: newObs,
				maneuverCodeToExclude: exclude,
			});
		} catch (error: any) {
			this.ratingAError = error.message || "Failed to calculate Rating A";
		}
	}

	testRatingB() {
		this.ratingBResult = null;
		this.ratingBError = null;

		try {
			const predefined: IObservationRating[] = JSON.parse(this.ratingB_predefined || "[]");
			const newObs: IObservationRating[] = JSON.parse(this.ratingB_new || "[]");
			const exclude: string[] = this.ratingB_exclude
				.split(",")
				.map((s) => s.trim())
				.filter((s) => s);

			// Uses the actual OfflineRatingService - any changes to the service will be reflected here
			this.ratingBResult = this.offlineRatingService.calculateRatingB({
				predefinedObs: predefined,
				newObs: newObs,
				maneuverCodeToExclude: exclude,
			});
		} catch (error: any) {
			this.ratingBError = error.message || "Failed to calculate Rating B";
		}
	}

	testRatingC() {
		this.ratingCResult = null;
		this.ratingCError = null;

		try {
			const scores: number[] = this.ratingC_scores
				.split(",")
				.map((s) => parseFloat(s.trim()))
				.filter((n) => !isNaN(n));

			// Uses the actual OfflineRatingService - any changes to the service will be reflected here
			this.ratingCResult = this.offlineRatingService.calculateRatingC(scores);
		} catch (error: any) {
			this.ratingCError = error.message || "Failed to calculate Rating C";
		}
	}

	loadSampleData() {
		const sampleDraft: DraftItem = {
			id: "test-draft-1",
			continuePage: 1,
			lastUpdatedDate: new Date().toISOString(),
			synced: false,
			toBeSynced: [],
			formDetails: {
				id: "form-1",
				name: "Test Form",
				startDate: new Date().toISOString(),
				endDate: new Date().toISOString(),
				formType: { code: "TS1", description: "Test Form Type" },
				sessionDetails: [],
				assessmentConfig: [
					{
						assessmentId: "assess-1",
						maneuverType: {
							code: "M1",
							description: "Maneuver 1",
						},
						observations: [
							{
								code: "OB1",
								attempt: 2,
								category: { code: "CAT1", description: "Category 1" },
								description: "Observation 1",
								id: "obs-1",
								isDisabled: false,
							},
							{
								code: "OB2",
								attempt: 1,
								category: { code: "CAT1", description: "Category 1" },
								description: "Observation 2",
								id: "obs-2",
								isDisabled: false,
							},
						],
					},
					{
						assessmentId: "assess-2",
						maneuverType: {
							code: "M2",
							description: "Maneuver 2",
						},
						observations: [
							{
								code: "OB3",
								attempt: 1,
								category: { code: "CAT2", description: "Category 2" },
								description: "Observation 3",
								id: "obs-3",
								isDisabled: false,
							},
						],
					},
				],
			},
			candidateRating: {
				temRate: {
					"candidate-1.assess-1": { rate: 4, assessmentId: "assess-1" },
					"candidate-1.assess-2": { rate: 3, assessmentId: "assess-2" },
				},
				addRemoveOb: [
					{
						candidateId: "candidate-1",
						observationCode: "OB4",
						maneuverCode: "M1",
						totalAttempt: 1,
						activeAttempt: 1,
						isRemoved: false,
						isRepeat: false,
						description: "New observation",
						candidateRole: "PILOT" as any,
						isDisabled: false,
						createdAt: new Date().toISOString(),
					},
				],
				selectedOBs: [],
				temEvaluations: [],
				resetManeuvers: [],
				otherComments: [],
			},
			trainingInformation: {
				candidates: [
					{
						candidateId: "candidate-1",
						isPrimary: true,
					},
				],
				assessmentDate: new Date().toISOString(),
				instructorId: "instructor-1",
			} as any,
			signOffDetails: {
				candidateSignOffDetails: {},
			} as any,
		};

		const jsonString = JSON.stringify(sampleDraft, null, 2);
		// Use the change handler to populate individual rating fields
		this.onDraftJsonChange(jsonString);
	}

	async fetchDraftsFromIndexedDB() {
		this.isLoadingDrafts = true;
		this.calculateError = null;

		try {
			// Fetch from cbtaDB (primary database)
			const cbtaDrafts = await firstValueFrom(this.dbService.getAll<DraftItem>(CONSTANT.INDEXEDDB.DRAFT));

			// Combine and deduplicate by id
			const draftsMap = new Map<string, DraftItem>();

			if (Array.isArray(cbtaDrafts)) {
				cbtaDrafts.forEach((draft) => {
					if (draft?.id) {
						draftsMap.set(draft.id, draft);
					}
				});
			}

			this.drafts = Array.from(draftsMap.values());

			if (this.drafts.length === 0) {
				this.calculateError = "No drafts found in IndexedDB. Make sure you have saved drafts.";
			}
		} catch (error: any) {
			this.calculateError = `Error fetching drafts: ${error.message || "Unknown error"}`;
			console.error("Error fetching drafts from IndexedDB:", error);
		} finally {
			this.isLoadingDrafts = false;
		}
	}

	async onDraftSelected() {
		if (!this.selectedDraftId) {
			return;
		}

		try {
			const draft = await firstValueFrom(
				this.dbService.getByID<DraftItem>(CONSTANT.INDEXEDDB.DRAFT, this.selectedDraftId),
			);

			if (draft) {
				// Use the change handler to populate individual rating fields
				const jsonString = JSON.stringify(draft, null, 2);
				this.onDraftJsonChange(jsonString);
			} else {
				this.calculateError = `Draft with ID "${this.selectedDraftId}" not found.`;
			}
		} catch (error: any) {
			this.calculateError = `Error loading draft: ${error.message || "Unknown error"}`;
			console.error("Error loading draft from IndexedDB:", error);
		}
	}

	private populateIndividualRatingFields(draft: DraftItem) {
		if (!draft || !this.candidateId) {
			return;
		}

		const candidateRating = draft?.candidateRating;
		if (!candidateRating) {
			return;
		}

		const temRate = candidateRating?.temRate ?? {};
		const temScores: any[] = [];

		// Extract scores for the candidate
		for (const key in temRate) {
			const cId = key.split(".")[0];
			const data = temRate[key];

			if (cId === this.candidateId) {
				temScores.push({
					...data,
					candidateId: cId,
				});
			}
		}

		// Get excluded maneuver IDs and non-zero rated scores
		const { zeroRated: excludeManeuverIds, nonZeroRated } = temScores.reduce<{
			zeroRated: string[];
			nonZeroRated: number[];
		}>(
			(acc, prevValue) => {
				if (prevValue.rate === 0 || prevValue.rate === -1) {
					acc.zeroRated.push(prevValue.assessmentId);
				} else {
					acc.nonZeroRated.push(prevValue.rate);
				}
				return acc;
			},
			{ zeroRated: [], nonZeroRated: [] },
		);

		// Get observation history
		const observationHistoryUnfiltered = (candidateRating?.addRemoveOb ?? []).filter((o) => {
			const isRepeat = o.isRepeat ?? false;
			return o.candidateId === this.candidateId && !isRepeat;
		});

		// Deduplicate observation history
		const observationHistoryMap: { [id: string]: any } = {};
		for (const o of observationHistoryUnfiltered) {
			observationHistoryMap[`${o.maneuverCode}.${o.observationCode}`] = o;
		}

		const observationHistory = Object.values(observationHistoryMap);

		const form = draft?.formDetails;
		const assessment = form?.assessmentConfig ?? [];

		// Get predefined observations and excluded maneuver codes
		const maneuverCodeToExclude: string[] = [];
		const [, predefinedObservations] = assessment.reduce<
			[
				string[],
				{
					id: string;
					categoryId: string;
					totalAttempt: number;
					activeAttempt: number;
					observationCode: string;
					maneuverCode: string;
				}[],
			]
		>(
			(acc, { maneuverType, observations, assessmentId }) => {
				if (excludeManeuverIds.includes(assessmentId)) {
					maneuverCodeToExclude.push(maneuverType.code);
				}

				if (!excludeManeuverIds.includes(assessmentId)) {
					observations.map((observation) => {
						acc[0].push(observation.code);
						acc[1].push({
							id: observation.code,
							categoryId: `${maneuverType.code}.${observation.category.code}`,
							totalAttempt: observation.attempt ?? 1,
							activeAttempt: observation.attempt ?? 1,
							observationCode: observation.code,
							maneuverCode: maneuverType.code,
						});
					});
				}
				return acc;
			},
			[[], []],
		);

		// Transform observation history
		const observationHistoryAltered = observationHistory.map((observation) => {
			return {
				id: observation.observationCode,
				maneuver: observation.maneuverCode,
				totalAttempt: observation.totalAttempt ?? 1,
				activeAttempt: observation.activeAttempt ?? 1,
				observationCode: observation.observationCode,
				maneuverCode: observation.maneuverCode,
				isRemoved: observation.isRemoved,
			};
		});

		// Format predefined observations
		const PredefinedObs: IObservationRating[] =
			predefinedObservations?.map((v) => {
				return {
					observationCode: v.observationCode,
					maneuverCode: v.maneuverCode,
					totalAttempt: v.totalAttempt,
					activeAttempt: v.activeAttempt,
					isRemoved: false,
				};
			}) || [];

		// Format new observations
		const NewObs: IObservationRating[] =
			observationHistoryAltered?.map((v) => {
				return {
					observationCode: v.observationCode,
					maneuverCode: v.maneuverCode,
					totalAttempt: v.totalAttempt,
					activeAttempt: v.activeAttempt,
					isRemoved: v.isRemoved,
				};
			}) || [];

		// Populate Rating A fields
		this.ratingA_predefined = JSON.stringify(PredefinedObs, null, 2);
		this.ratingA_new = JSON.stringify(NewObs, null, 2);
		this.ratingA_exclude = [...new Set(maneuverCodeToExclude)].join(", ");

		// Populate Rating B fields
		this.ratingB_predefined = JSON.stringify(PredefinedObs, null, 2);
		this.ratingB_new = JSON.stringify(NewObs, null, 2);
		this.ratingB_exclude = [...new Set(maneuverCodeToExclude)].join(", ");

		// Populate Rating C field (scores)
		this.ratingC_scores = nonZeroRated.length > 0 ? nonZeroRated.join(", ") : "";
	}

	onDraftJsonChange(jsonString: string) {
		// Update the draftDataJson property
		this.draftDataJson = jsonString;

		// Try to parse and populate individual rating fields
		if (!jsonString || !jsonString.trim()) {
			// Clear fields if JSON is empty
			this.clearIndividualRatingFields();
			return;
		}

		try {
			const draft: DraftItem = JSON.parse(jsonString);

			// Validate it's a draft object
			if (draft && typeof draft === "object") {
				// Auto-set candidate ID if available
				const candidates = draft.trainingInformation?.candidates;
				if (candidates && candidates.length > 0) {
					const primaryCandidate = candidates.find((c) => c.isPrimary) || candidates[0];
					if (primaryCandidate?.candidateId) {
						this.candidateId = primaryCandidate.candidateId;
					}
				}

				// Populate individual rating fields
				this.populateIndividualRatingFields(draft);
			}
		} catch (error) {
			// Invalid JSON - don't populate fields, but don't show error either
			// User might be in the middle of typing
			console.debug("Draft JSON parse error (might be incomplete):", error);
		}
	}

	private clearIndividualRatingFields() {
		this.ratingA_predefined = "";
		this.ratingA_new = "";
		this.ratingA_exclude = "";
		this.ratingB_predefined = "";
		this.ratingB_new = "";
		this.ratingB_exclude = "";
		this.ratingC_scores = "";
	}

	getDraftDisplayText(draft: DraftItem): string {
		const date = draft.trainingInformation?.assessmentDate;
		if (date) {
			try {
				const formattedDate = new Date(date).toLocaleString("en-US", {
					month: "short",
					day: "numeric",
					year: "numeric",
					hour: "numeric",
					minute: "2-digit",
				});
				return `${draft.id} - ${formattedDate}`;
			} catch {
				return `${draft.id} - Invalid date`;
			}
		}
		return `${draft.id} - No date`;
	}
}
